export const CONTAINER = "max-w-[1240px] mx-auto px-6 lg:px-8";

export const SERVICE_ORDER = ["managed-it", "connectivity", "cyber", "cloud", "network", "surveillance"];

// Homepage hero defaults — shared by the public hero and the admin editor, so a
// blank override renders the hero exactly as shipped.
export const HERO_DEFAULTS = {
  badge: "Next-Generation IT",
  headingPre: "We produce end-to-end IT ",
  headingHighlight: "integration",
  headingPost: ", support, and consulting solutions",
  sub: "Empowering ambitious businesses with high-fidelity cyber security, seamless hybrid-cloud integration and ironclad, London-based technical support infrastructures.",
  primaryLabel: "Explore Solutions",
  primaryHref: "/managed-it",
  secondaryLabel: "Contact Sales",
  secondaryHref: "#contact",
  features: ["No lock-in contracts", "4-minute avg response", "Real engineers, not scripts"],
  image: "/brain-hero.webp",
  imageAlt: "Neural IT intelligence",
};

// Stats band — { n: value, l: label }.
export const STATS_DEFAULTS = [
  { n: "4min", l: "average response time" },
  { n: "99.98%", l: "uptime across managed clients" },
  { n: "24/7", l: "London-based helpdesk" },
  { n: "5000+", l: "endpoints under management" },
];

// Process timeline — heading/intro + numbered steps (numbers auto-generated).
export const PROCESS_DEFAULTS = {
  eyebrow: "how it works",
  heading: "Onboarding without the drama",
  intro: "A proven four-step path from first audit to a fully managed, continuously optimised IT estate.",
  steps: [
    { icon: "Search", t: "Discover", d: "We audit your estate — devices, licences, security posture and the gaps nobody told you about." },
    { icon: "Rocket", t: "Onboard", d: "Zero-downtime transition. We document everything and deploy monitoring across your fleet." },
    { icon: "Wrench", t: "Manage", d: "Proactive patching, 24/7 monitoring and a helpdesk your team will actually enjoy calling." },
    { icon: "LineChart", t: "Optimise", d: "Quarterly reviews with a dedicated vCIO to keep IT aligned with where the business is heading." },
  ],
};

// "Why" panel — badge/heading + points, and the testimonial cards beside it.
export const WHY_DEFAULTS = {
  badge: "WHY CLI SECURE",
  heading: "Why teams switch to CLI Secure",
  points: [
    { icon: "Clock", t: "Actually responsive", d: "A 4-minute average response and a helpdesk that picks up the phone." },
    { icon: "ShieldCheck", t: "Security by default", d: "EDR, MDR and 24/7 SOC baked in — not sold as an afterthought." },
    { icon: "TrendingUp", t: "Aligned to growth", d: "A dedicated vCIO who plans IT around your business, not the other way round." },
    { icon: "Zap", t: "One accountable partner", d: "From cabling to cloud, no more finger-pointing between vendors." },
  ],
};

export const TESTIMONIALS_DEFAULTS = [
  { q: "We moved 80 staff to CLI Secure with zero downtime. Tickets that used to sit for days now close in hours.", n: "Priya Nair", r: "Operations Director, Meridian Legal", img: "https://images.pexels.com/photos/31869537/pexels-photo-31869537.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { q: "Finally an IT partner that speaks plain English. The quarterly reviews genuinely shape our roadmap.", n: "Daniel Osei", r: "Managing Partner, Northgate Capital", img: "https://images.pexels.com/photos/4797690/pexels-photo-4797690.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

// Trust strip — heading + scrolling partner names.
export const TRUST_DEFAULTS = {
  heading: "powered by best-in-class technology",
  partners: ["SentinelOne", "Fortinet", "Microsoft 365", "Cisco", "Aruba", "Guardz", "Mirket", "Eagle Eye", "Voipinger", "Cloudflare", "Lenovo", "Openreach", "Vodafone", "BT"],
};

// Services bento — section copy + per-service card blurb/image (cards link to the
// nav entry, so labels/links are edited under Menus).
export const SERVICES_DEFAULTS = {
  eyebrow: "what we do",
  heading: "One London partner for managed IT, cyber security & cloud",
  intro: "From day-to-day helpdesk and Microsoft 365 to 24/7 cyber security, resilient networks and business-grade connectivity — CLI Secure keeps every layer of your technology running, secured and accountable to a single London-based team.",
  cards: {
    "managed-it": { blurb: "A proactive, London-based helpdesk and vCIO team that keeps your people productive and your IT ahead of the business.", image: "/svc-managed-it-xs.webp" },
    cyber: { blurb: "Endpoint, network, email and identity protection watched around the clock by a real UK Security Operations Centre.", image: "/svc-cyber-security-xs.webp" },
    cloud: { blurb: "Microsoft 365, SharePoint and Teams migration, management and backup — secured and cost-optimised.", image: "/svc-cloud-microsoft-xs.webp" },
    network: { blurb: "Vendor-certified Cisco, Aruba and Fortinet design, deployment and Wi-Fi that performs everywhere.", image: "/svc-network-consultancy-xs.webp" },
    surveillance: { blurb: "Certified structured cabling and cloud-managed CCTV you can monitor securely from anywhere.", image: "/svc-surveillance-data-xs.webp" },
    connectivity: { blurb: "Full-fibre broadband, leased lines, business mobile and cloud telephony backed by real SLAs.", image: "/svc-connectivity-telecoms-xs.webp" },
  },
};

// CLI 360 flagship feature block — copy, feature list and CTAs (the live
// dashboard mockup beside it stays as a decorative graphic).
export const CLI360_DEFAULTS = {
  badge: "FLAGSHIP PLATFORM",
  headingPre: "CLI ",
  headingHighlight: "360",
  headingPost: " — your whole IT estate on one pane of glass",
  sub: "Support, security, cloud and automation converge in a single managed platform. Real-time visibility for you, proactive action for us — no more logging into ten different tools.",
  features: [
    { icon: "Activity", t: "Live asset & security dashboard" },
    { icon: "Check", t: "Automated patching & compliance" },
    { icon: "Server", t: "Unified device & cloud inventory" },
    { icon: "TrendingUp", t: "vCIO roadmap & quarterly reviews" },
  ],
  primaryLabel: "See it inside Managed IT",
  primaryHref: "/managed-it",
  secondaryLabel: "Book a live demo",
  secondaryHref: "/contact-us",
};

export const HOME_FAQS = [
  { q: "What does a managed IT service in London actually include?", a: "Our managed IT service covers your entire estate — a 24/7 London-based helpdesk, proactive monitoring and patching, Microsoft 365 and cloud management, backup and disaster recovery, cyber security, network and connectivity, plus a dedicated vCIO for strategy. One accountable team, one predictable monthly fee." },
  { q: "How quickly do you respond to IT support requests?", a: "We answer with a 4-minute average response time and resolve the majority of issues remotely the same day. Managed clients get 24/7 cover, so critical incidents are handled at any hour by real UK engineers — never an offshore script." },
  { q: "Do you provide cyber security as part of managed IT?", a: "Yes. Security is built in, not bolted on. Every managed plan includes endpoint protection (EDR), email and identity security, and 24/7 monitoring from a real UK Security Operations Centre, aligned to Cyber Essentials and ISO 27001." },
  { q: "Can you switch us over from our current IT provider without downtime?", a: "Absolutely. Our onboarding is designed for zero downtime — we audit your estate, document everything, deploy monitoring and migrate services out of hours, so your team keeps working throughout the transition." },
  { q: "Are there long-term lock-in contracts?", a: "No. We earn your business every month with responsive support and clear reporting. Flexible terms mean you stay because the service is genuinely good, not because a contract traps you." },
];
